/**
 * Canvas zoom chrome — must sit above Cytoscape's transparent canvas (zIndex: 1).
 * Native `title` tooltips alone are unreliable under that layer; these use hover/focus tips.
 */
import React from 'react';
import { ZoomIn, ZoomOut, Maximize2, RotateCcw } from 'lucide-react';

function ZoomControlButton({
    label,
    onClick,
    children,
}: {
    label: string;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            data-testid={`canvas-zoom-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="group relative p-1.5 hover:bg-secondary rounded transition-colors text-muted-foreground/80 hover:text-foreground"
        >
            {children}
            <span
                role="tooltip"
                className="pointer-events-none absolute left-full top-1/2 z-30 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-[11px] font-medium text-popover-foreground opacity-0 shadow-overlay transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100"
            >
                {label}
            </span>
        </button>
    );
}

export interface CanvasZoomControlsProps {
    zoomLevel: number;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onFitToView: () => void;
    onResetZoom: () => void;
}

export function CanvasZoomControls({
    zoomLevel,
    onZoomIn,
    onZoomOut,
    onFitToView,
    onResetZoom,
}: CanvasZoomControlsProps) {
    return (
        <div
            className="absolute bottom-4 left-4 z-20 flex flex-col gap-1 bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-overlay p-1 pointer-events-auto"
            data-tour="canvas-zoom"
            data-testid="canvas-zoom-controls"
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <ZoomControlButton label="Zoom In" onClick={onZoomIn}>
                <ZoomIn className="w-4 h-4" />
            </ZoomControlButton>
            <ZoomControlButton label="Zoom Out" onClick={onZoomOut}>
                <ZoomOut className="w-4 h-4" />
            </ZoomControlButton>
            <div className="h-px bg-border/60 my-0.5" />
            <span className="text-[10px] text-center text-muted-foreground font-medium py-0.5" aria-live="polite">
                {zoomLevel}%
            </span>
            <div className="h-px bg-border/60 my-0.5" />
            <ZoomControlButton label="Fit to View" onClick={onFitToView}>
                <Maximize2 className="w-4 h-4" />
            </ZoomControlButton>
            <ZoomControlButton label="Reset Zoom (100%)" onClick={onResetZoom}>
                <RotateCcw className="w-4 h-4" />
            </ZoomControlButton>
        </div>
    );
}
